<script>
import { ref, computed, onMounted } from 'vue';
import { VueRecaptcha } from 'vue-recaptcha';
import axios from 'axios';

export default {
  name: 'InitialForm',
  components: {
    VueRecaptcha,
  },
  emits: ['form-submitted'],
  setup(props, { emit }) {
    const email = ref('');
    const primoCheckbox = ref(false);
    const secondoCheckbox = ref(false);
    const recaptchaToken = ref('');
    const siteKeyreCAPTCHA = ref('6LeYjQIqAAAAAHn46tFsq66aZkGrWo6FwfRzeKiV');
    const contatore = ref(null);
    const limitModal = ref(null);
    const recaptchaRef = ref(null);

    const isFormValid = computed(() => {
      return email.value !== '' && primoCheckbox.value && secondoCheckbox.value;
    });

    const handleSuccess = (token) => {
      console.log('reCAPTCHA token received:', token);
      recaptchaToken.value = token;
      submitForm();
    };

    const handleError = () => {
      recaptchaToken.value = '';
    };

    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return null;
    };

    const setCookie = (name, value, days) => {
      let expires = "";
      if (days) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        expires = "; expires=" + date.toUTCString();
      }
      document.cookie = `${name}=${value || ""}${expires}; path=/`;
      console.log(`Cookie set: ${name}=${value || ""}${expires}; path=/`);
    };

    const checkLimit = () => {
      const today = new Date().toISOString().slice(0, 10);
      let checks = JSON.parse(getCookie('checks') || '{}');

      console.log('Current checks cookie:', checks); // Debug log

      if (checks.date !== today) {
        checks = { date: today, count: 0 };
      }

      if (checks.count >= 5) {
        console.log('Check limit reached');
        return false;
      } else {
        checks.count += 1;
        setCookie('checks', JSON.stringify(checks), 1);
        console.log('Updated checks cookie:', checks); // Debug log
        return true;
      }
    };

    const submitForm = async () => {
      if (recaptchaToken.value === '') {
        // Trigger reCAPTCHA verification if the token is not yet available
        recaptchaRef.value.execute();
        return;
      }

      if (!isFormValid.value || !checkLimit()) {
        if (!checkLimit()) {
          limitModal.value.show();
        }
        return;
      }

      try {
        const response = await axios.post(`${process.env.VUE_APP_BACKEND_URL}/api/check-breaches/`, {
          email: email.value
        });
        emit('form-submitted', { breaches: response.data, email: email.value });
      } catch (error) {
        if (error.response && error.response.status === 404) {
          emit('form-submitted', { breaches: null, email: email.value });
        } else {
          console.error('Error:', error);
        }
      }
    };

    const hideModal = () => {
      limitModal.value.hide();
    };

    const contatoreClass = ref(''); // Classe per il lampeggio

    const startCounter = () => {
      const updateCounter = () => {
        const startNumber = Math.floor(Date.now() / 100 - 2200000000 + Math.random() * 12);
        contatore.value = startNumber.toLocaleString(undefined, { minimumFractionDigits: 0 });
        window.localStorage.setItem('start', startNumber);

        // Aggiungi la classe lampeggiante e rimuovila dopo 300ms
        contatoreClass.value = 'blink';
        setTimeout(() => {
          contatoreClass.value = '';
        }, 300);
      };

      updateCounter();
      setInterval(updateCounter, 1600);
    };

    onMounted(() => {
      startCounter();
    });

    return {
      email,
      primoCheckbox,
      secondoCheckbox,
      recaptchaToken,
      siteKeyreCAPTCHA,
      isFormValid,
      handleSuccess,
      handleError,
      submitForm,
      contatore,
      limitModal,
      hideModal,
      recaptchaRef,
      contatoreClass 
    };
  }
};
</script>

<template>
  <div class="padded-center">
    <h1 class="mb-4 mt-3 text-left">SCOPRI SUBITO SE LA TUA PASSWORD È STATA RUBATA</h1>
    <p class="mb-4">
      Ogni giorno si verificano violazioni di dati online. E-mail, password e altre informazioni sensibili possono finire nelle mani di malintenzionati.
    </p>
    <p class="mb-4">
      Grazie a questo controllo, puoi verificare in pochi secondi se le tue credenziali sono state esposte, proteggendo così la tua sicurezza digitale.
    </p>
    <p class="mb-4">
      I tuoi dati potrebbero essere stati pubblicati sul Web, Deep Web o Dark Web. Verifica in pochi secondi, senza inserire alcuna password o dato personale. La tua privacy è al sicuro.
    </p>
    <p class="mb-4"><strong>I tuoi dati potrebbero essere in pericolo. Controlla ora per proteggerti!</strong></p>
    <h4 class="mb-2 text-left">Questo controllo ti permette di:</h4>
    <ul class="mb-2 check" style="list-style: none; padding: 0;">
      <li style="margin-bottom: 10px;"><span class="mr-2">✔</span> <b>Verifica immediata</b>: Saprai subito se il tuo account è stato coinvolto in una violazione</li>
      <li style="margin-bottom: 10px;"><span class="mr-2">✔</span> <b>Controllo approfondito</b>: I tuoi dati potrebbero trovarsi su Web, Deep Web o Dark Web</li>
      <li style="margin-bottom: 10px;"><span class="mr-2">✔</span> <b>Risultato rapido</b>: Scopri subito se la tua password è stata rubata</li>
    </ul>
    <h4 class="mb-2 text-left">Questo controllo:</h4>
    <ul class="mb-2 cross" style="list-style: none; padding: 0;">
      <li style="margin-bottom: 10px;"><span class="mr-2">✖</span><span> <b>NON</b> richiede <b>NESSUNA</b> password</span></li>
      <li style="margin-bottom: 10px;"><span class="mr-2">✖</span><span> <b>NON</b> richiede <b>NESSUN</b> dato personale</span></li>
    </ul>
    <h5 class="mb-4">
      Ci sono anche le tue tra le
      <span id="contatore1" class="contatore-violazioni" :class="contatoreClass" style="color: red; font-weight: bold; font-size: 1.4rem;">{{ contatore }}</span>
      informazioni rubate?
    </h5>
    <h4 class="mb-4">Scoprilo subito, basta inserire la tua e-mail:</h4>
    <p style="color: #ffd700">
      <i class="ri-error-warning-fill"></i>
      Per favore <b>NON</b> inserire e-mail di altre persone senza autorizzazione
      <i class="ri-error-warning-fill"></i>
    </p>
    <div class="input-group mb-3">
      <span class="input-group-text"><i class="ri-mail-fill me-2"></i>E-mail</span>
      <input type="text" class="form-control" v-model="email" placeholder="Inserisci qui la tua e-mail" autocomplete="off" required>
    </div>
    <div class="form-check mb-3">
      <input class="form-check-input" type="checkbox" v-model="primoCheckbox" id="consentCheckbox">
      <label class="form-check-label" for="consentCheckbox">Autorizzo l'invio di e-mail su come difendersi dagli hacker.</label>
    </div>
    <div class="form-check mb-3">
      <input class="form-check-input" type="checkbox" v-model="secondoCheckbox" id="tipsConsentCheckbox">
      <label class="form-check-label" for="tipsConsentCheckbox">Confermo che questa e-mail mi appartiene o che ho il permesso di utilizzarla.</label>
    </div>
    <VueRecaptcha v-if="siteKeyreCAPTCHA" ref="recaptchaRef" :sitekey="siteKeyreCAPTCHA" size="invisible" @verify="handleSuccess" @error="handleError" />
    <BButton :disabled="!isFormValid" @click="submitForm" variant="success">Clicca qui per verificare la tua e-mail</BButton>

    <b-modal id="limitModal" hide-footer ref="limitModal" modal-class="zoomIn" centered size="lg" content-class="bg-dark text-white">
      <div class="text-center">
        <div class="fs-15 mx-4 mx-sm-5 mb-4">
          <h3>Limite raggiunto</h3>
          <p>Hai raggiunto il limite massimo di 5 verifiche per oggi. Riprova domani.</p>
        </div>
      </div>
      <div class="text-center">
        <b-button variant="danger" @click="hideModal">Chiudi</b-button>
      </div>
    </b-modal>
  </div>
</template>

<style scoped>
.padded-center {
  padding-left: 5%;
  padding-right: 5%;
  text-align: left;
}

.avvertenza {
  color: #ffd700;
}

.text-left {
    text-align: left;
  }
  
ul.cross, ul.check {
  background: transparent;
  list-style: none;

}

.mr-2 {
  margin-right: 10px;
}

.check .mr-2 {
  color: #66ff00;
  font-size: 20px;
}

.cross .mr-2 {
  color: red;
  font-size: 25px;
}

img {
  max-width: 100%;
  max-height: 100px;
}

ul {
  width: 90%;
  margin: 0 auto;
  text-align: left;
}

.cross li, .check li {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  font-size: 18px;
}

.blink {
  transition: all 200ms ease;
  animation: blink 300ms linear;
}

@keyframes blink {
  50% {
    opacity: .5;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
